import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  IPurchaseInitialState,
  IPurchaseDetail,
  IFilterPurchase,
  IPurchaseFormArray,
} from "./purchaseModal";
import {
  createPurchase,
  deletePurchase,
  findAllActivePurchases,
  getAllPurchases,
  getPurchaseById,
  purchaseActiveInactive,
  updatePurchase,
} from "./services/purchase.services";
import moment from "moment";

const initialState: IPurchaseInitialState = {
  loading: false,
  formLoading: false,
  purchaseDrawerOpen: false,
  error: "",
  PurchaseList: [],
  PurchaseID: null,
  purchaseChildIndex: -1,
  purchasePreviousChild: null,
  PurchaseDetail: {
    ID: undefined,
    Date: moment().toDate(),
    VeparyID: null,
    InvoiceNo: null,
    PurchaseType: "Antique",
    IsActive: 1,
    PurchaseForm: [],
  },
  FilterPurchase: {
    Date: "",
    VeparyID: null,
    InvoiceNo: null,
  },
  PurchaseItemIndex: -1,
  PurchaseOrderIndex: -1,
  PurchaseItemDeleteConfirmDialog: false,
  PurchasePreviousOrder: null,
  PurchaseSave: false,
};

export const purchaseSlice = createSlice({
  name: "purchase",
  initialState,
  reducers: {
    setPurchaseChildIndex: (state, action: PayloadAction<number>) => {
      state.purchaseChildIndex = action.payload;
    },
    setPurchasePreviousChild: (state, action: PayloadAction<any>) => {
      state.purchasePreviousChild = action.payload;
    },
    clearPurchaseChildState: (state) => {
      state.purchaseChildIndex = -1;
      state.purchasePreviousChild = null;
    },
    clearPurchaseDetails: (state) => {
      state.PurchaseDetail = initialState.PurchaseDetail;
    },
    setPurchaseID: (state, action: PayloadAction<number>) => {
      state.PurchaseID = action.payload;
    },
    clearPurchaseID: (state) => {
      state.PurchaseID = null;
    },
    setFilterPurchase: (state, action: PayloadAction<IFilterPurchase>) => {
      state.FilterPurchase = action.payload;
    },
    clearFilterPurchase: (state) => {
      state.FilterPurchase = initialState.FilterPurchase;
    },
    openPurchaseDrawer: (state) => {
      state.purchaseDrawerOpen = true;
    },
    closePurchaseDrawer: (state) => {
      state.purchaseDrawerOpen = false;
    },
    setPurchaseDetail: (state, action: PayloadAction<IPurchaseDetail>) => {
      state.PurchaseDetail = action.payload;
    },
    setPurchaseItemIndex: (state, action: PayloadAction<number>) => {
      state.PurchaseItemIndex = action.payload;
    },
    setPurchaseOrderIndex: (state, action: PayloadAction<number>) => {
      state.PurchaseOrderIndex = action.payload;
    },
    clearPurchaseItemIndex: (state) => {
      state.PurchaseItemIndex = -1;
    },
    clearPurchaseOrderIndex: (state) => {
      state.PurchaseOrderIndex = -1;
    },
    openPurchaseItemDeleteConfirmDialog: (state) => {
      state.PurchaseItemDeleteConfirmDialog = true;
    },
    openPurchaseOrderDeleteConfirmDialog: (state) => {
      state.PurchaseItemDeleteConfirmDialog = true;
    },
    closePurchaseItemDeleteConfirmDialog: (state) => {
      state.PurchaseItemDeleteConfirmDialog = false;
    },
    closePurchaseOrderDeleteConfirmDialog: (state) => {
      state.PurchaseItemDeleteConfirmDialog = false;
    },
    setPurchasePreviousOrder: (
      state,
      action: PayloadAction<IPurchaseFormArray | null>,
    ) => {
      state.PurchasePreviousOrder = action.payload;
    },
    setPurchaseSave: (state, action: PayloadAction<boolean>) => {
      state.PurchaseSave = action.payload;
    },
    setPurchaseLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Get All Purchases
    builder.addCase(getAllPurchases.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      getAllPurchases.fulfilled,
      (state, action: PayloadAction<IPurchaseDetail[]>) => {
        state.loading = false;
        state.PurchaseList = action.payload || [];
      },
    );
    builder.addCase(getAllPurchases.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.PurchaseList = [];
    });

    // Find All Active Purchases
    builder.addCase(findAllActivePurchases.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      findAllActivePurchases.fulfilled,
      (state, action: PayloadAction<IPurchaseDetail[]>) => {
        state.loading = false;
        state.PurchaseList = action.payload || [];
      },
    );
    builder.addCase(findAllActivePurchases.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.PurchaseList = [];
    });

    // Create Purchase
    builder.addCase(createPurchase.pending, (state) => {
      state.formLoading = true;
      state.error = "";
    });
    builder.addCase(createPurchase.fulfilled, (state) => {
      state.formLoading = false;
      state.purchaseDrawerOpen = false;
    });
    builder.addCase(createPurchase.rejected, (state, action) => {
      state.formLoading = false;
      state.error = action.error.message || "Something went wrong";
    });

    // Update Purchase
    builder.addCase(updatePurchase.pending, (state) => {
      state.formLoading = true;
      state.error = "";
    });
    builder.addCase(updatePurchase.fulfilled, (state) => {
      state.formLoading = false;
      state.purchaseDrawerOpen = false;
    });
    builder.addCase(updatePurchase.rejected, (state, action) => {
      state.formLoading = false;
      state.error = action.error.message || "Something went wrong";
    });

    // Delete Purchase
    builder.addCase(deletePurchase.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(deletePurchase.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(deletePurchase.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
    });

    // Purchase Active/Inactive
    builder.addCase(purchaseActiveInactive.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(purchaseActiveInactive.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(purchaseActiveInactive.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
    });

    // Get Purchase By ID
    builder.addCase(getPurchaseById.pending, (state) => {
      state.formLoading = true;
      state.error = "";
    });
    builder.addCase(
      getPurchaseById.fulfilled,
      (state, action: PayloadAction<IPurchaseDetail>) => {
        state.formLoading = false;
        state.PurchaseDetail = action.payload;
      },
    );
    builder.addCase(getPurchaseById.rejected, (state, action) => {
      state.formLoading = false;
      state.PurchaseDetail = initialState.PurchaseDetail;
      state.error = action.error.message || "Something went wrong";
    });
  },
});

export const {
  setPurchaseChildIndex,
  setPurchasePreviousChild,
  clearPurchaseChildState,
  clearPurchaseDetails,
  setPurchaseID,
  clearPurchaseID,
  clearFilterPurchase,
  setFilterPurchase,
  closePurchaseDrawer,
  openPurchaseDrawer,
  setPurchaseDetail,
  setPurchaseItemIndex,
  clearPurchaseItemIndex,
  openPurchaseItemDeleteConfirmDialog,
  closePurchaseItemDeleteConfirmDialog,
  openPurchaseOrderDeleteConfirmDialog,
  closePurchaseOrderDeleteConfirmDialog,
  setPurchaseOrderIndex,
  clearPurchaseOrderIndex,
  setPurchasePreviousOrder,
  setPurchaseSave,
  setPurchaseLoading,
} = purchaseSlice.actions;

export default purchaseSlice.reducer;
